import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
} from "react";
import {
    Grid,
    Card,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Box,
    IconButton,
    Chip,
    InputAdornment,
    CircularProgress,
    Alert,
    Snackbar,
    Autocomplete,
    Pagination,
} from "@mui/material";

import {
    Star as StarIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Settings as SettingsIcon,
    Group as GroupIcon,
    DirectionsCar as CarIcon,
} from "@mui/icons-material";
import { colors } from "../components/Theme/WebTheme";
import { FirebaseContext } from "common";
import LoyaltyAPI from "../common/loyaltyApi";
import { useTranslation } from "react-i18next";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const LoyaltyPoints = () => {
    const { t } = useTranslation();
    const { auth } = useContext(FirebaseContext);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("All");
    const [selectedUser, setSelectedUser] = useState("");
    const [pointsAmount, setPointsAmount] = useState("");
    const [pointsAction, setPointsAction] = useState("add");
    const [configOpen, setConfigOpen] = useState(false);
    const [configTab, setConfigTab] = useState(0);

    // API integration state
    const [apiConfig, setApiConfig] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalCustomers: 0,
        totalDrivers: 0,
        totalPoint: 0,
    });

    // Configuration saving state
    const [saving, setSaving] = useState(false);
    const [modifiedActions, setModifiedActions] = useState({});

    // Date and time range state
    const [modifiedDateRanges, setModifiedDateRanges] = useState({});
    const [modifiedTimeRanges, setModifiedTimeRanges] = useState({});
    const [modifiedRatings, setModifiedRatings] = useState({});

    // Authentication and error state
    const [authError, setAuthError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [maxRetries] = useState(3);

    // Notification state
    const [notification, setNotification] = useState({
        open: false,
        type: "success", // 'success' | 'error'
        message: "",
    });

    // Points history state
    const [historyLoading, setHistoryLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
        grouped: true,
    });

    // Users list state
    const [usersList, setUsersList] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [selectedUserObject, setSelectedUserObject] = useState(null);

    // Users pagination state for autocomplete
    const [usersPagination, setUsersPagination] = useState({
        page: 1,
        limit: 20,
        hasMoreData: true,
        searchTerm: "",
    });
    const [usersLoadingMore, setUsersLoadingMore] = useState(false);
    const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [wasSearching, setWasSearching] = useState(false);
    const autocompleteInputRef = useRef(null);

    // Points submission state
    const [submittingPoints, setSubmittingPoints] = useState(false);

    // Grouped points state
    const [groupedPoints, setGroupedPoints] = useState([]);
    const [selectedUserDetail, setSelectedUserDetail] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [userHistoryLoading, setUserHistoryLoading] = useState(false);
    const [userHistory, setUserHistory] = useState([]);
    const [userHistoryPagination, setUserHistoryPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
    });

    const totalCustomers = stats.totalCustomers;
    const totalDrivers = stats.totalDrivers;
    const totalPoints = stats.totalPoint;

    // Check if user is authenticated
    const isAuthenticated = auth && auth.currentUser;

    // Enhanced error handling function
    const handleApiError = useCallback((error, operation) => {
        console.error(`Error in ${operation}:`, error);

        if (
            error.message?.includes("Authentication failed") ||
            error.message?.includes("User not authenticated")
        ) {
            setAuthError(
                "Authentication required. Please refresh the page and try again."
            );
            setNotification({
                open: true,
                type: "error",
                message:
                    "Authentication required. Please refresh the page and try again.",
            });
            return;
        }

        if (error.message?.includes("Network")) {
            setNotification({
                open: true,
                type: "error",
                message:
                    "Network error. Please check your connection and try again.",
            });
            return;
        }

        // Generic error handling
        setNotification({
            open: true,
            type: "error",
            message: `Failed to ${operation}. Please try again.`,
        });
    }, []);

    // Retry function for failed API calls
    const retryApiCall = useCallback(
        async (apiFunction, ...args) => {
            let lastError;

            for (let i = 0; i <= maxRetries; i++) {
                try {
                    const result = await apiFunction(...args);
                    setRetryCount(0); // Reset retry count on success
                    return result;
                } catch (error) {
                    lastError = error;

                    if (i < maxRetries) {
                        // Wait before retrying (exponential backoff)
                        const delay = Math.min(1000 * Math.pow(2, i), 5000);
                        await new Promise((resolve) =>
                            setTimeout(resolve, delay)
                        );
                        setRetryCount(i + 1);
                    }
                }
            }

            throw lastError;
        },
        [maxRetries]
    );

    // Wait for authentication to be ready
    const waitForAuth = useCallback(async () => {
        if (isAuthenticated) {
            return true;
        }

        // Wait up to 10 seconds for authentication
        for (let i = 0; i < 100; i++) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            if (auth && auth.currentUser) {
                return true;
            }
        }

        throw new Error("Authentication timeout");
    }, [isAuthenticated, auth]);

    const fetchLoyaltyConfig = useCallback(async () => {
        try {
            const config = await LoyaltyAPI.getLoyaltyPointsConfig();
            setApiConfig(config);
        } catch (err) {
            console.error("Error fetching loyalty config:", err);
            // Fallback to default values if API fails
            setApiConfig([]);
            throw err;
        }
    }, []);

    const fetchLoyaltyStats = useCallback(async () => {
        try {
            const statsData = await LoyaltyAPI.getLoyaltyStats();
            setStats({
                totalCustomers: statsData.totalCustomers || 0,
                totalDrivers: statsData.totalDrivers || 0,
                totalPoint: statsData.totalPoints || 0,
            });
        } catch (err) {
            console.error("Error fetching loyalty stats:", err);
            // Keep default values if API fails
            throw err;
        }
    }, []);

    const fetchUsers = useCallback(
        async (searchTerm = "", page = 1, freshSearch = false) => {
            try {
                if (freshSearch) {
                    setUsersLoading(true);
                    setIsSearching(true);
                    setUsersList([]);
                    // Keep autocomplete open during search and remember we were searching
                    setAutocompleteOpen(true);
                    setWasSearching(true);
                } else {
                    setUsersLoadingMore(true);
                }

                console.log("🔄 Fetching users for dropdown...", {
                    searchTerm,
                    page,
                    freshSearch,
                    limit: usersPagination.limit,
                });

                const params = {
                    page: page,
                    per_page: usersPagination.limit,
                };

                // Only add search parameter if searchTerm is not empty
                if (searchTerm && searchTerm.trim() !== "") {
                    params.search = searchTerm.trim();
                }

                console.log("📡 API params:", params);
                const usersResponse = await LoyaltyAPI.getUsers(params);

                console.log("📥 Users API response:", usersResponse);

                // Extract data from response (API returns {data: [...], pagination: {...}})
                const usersData = usersResponse.data || usersResponse;
                console.log(
                    "👥 Processed users data:",
                    usersData,
                    "isArray:",
                    Array.isArray(usersData)
                );

                const normalizedData = Array.isArray(usersData)
                    ? usersData
                    : [];

                if (freshSearch) {
                    setUsersList(normalizedData);
                    console.log(
                        "🔄 Fresh search - replaced usersList with",
                        normalizedData.length,
                        "users"
                    );
                    setAutocompleteOpen(true);
                } else {
                    setUsersList((prev) => {
                        const newList = [...prev, ...normalizedData];
                        console.log(
                            "➕ Load more - added",
                            normalizedData.length,
                            "users, total now:",
                            newList.length
                        );
                        return newList;
                    });
                }

                // Update pagination state
                const fetchedCount = normalizedData.length;
                const hasMore = fetchedCount >= usersPagination.limit;

                setUsersPagination((prev) => ({
                    ...prev,
                    page: page,
                    hasMoreData: hasMore,
                    searchTerm: searchTerm,
                }));

                console.log(
                    "✅ Users list updated successfully, count:",
                    fetchedCount,
                    "hasMore:",
                    hasMore,
                    "searchTerm:",
                    searchTerm
                );
                setTimeout(() => {
                    const inputElement =
                        autocompleteInputRef.current?.querySelector("input");
                    if (inputElement) {
                        inputElement.focus();
                        // Set cursor to end of text
                        const textLength = inputElement.value.length;
                        inputElement.setSelectionRange(textLength, textLength);
                    }
                }, 150);
            } catch (err) {
                console.error("❌ Error fetching users:", err);
                if (freshSearch) {
                    setUsersList([]);
                }
                throw err;
            } finally {
                setUsersLoading(false);
                setUsersLoadingMore(false);
                setIsSearching(false);
                // Keep autocomplete open after search loading completes
                // Only if we were actively searching (not just loading more)
                if (wasSearching && freshSearch) {
                    setAutocompleteOpen(true);
                    setWasSearching(false); // Reset the flag
                }
            }
        },
        [usersPagination.limit]
    );

    // Load more users for autocomplete
    const loadMoreUsers = useCallback(async () => {
        if (!usersLoadingMore && usersPagination.hasMoreData) {
            const nextPage = usersPagination.page + 1;
            console.log("🔄 Loading more users, page:", nextPage);
            await fetchUsers(usersPagination.searchTerm, nextPage, false);
        }
    }, [
        usersLoadingMore,
        usersPagination.hasMoreData,
        usersPagination.page,
        usersPagination.searchTerm,
        fetchUsers,
    ]);

    // Handle search in autocomplete with debounce
    const [searchTimeout, setSearchTimeout] = useState(null);

    const handleUsersSearch = useCallback(
        async (searchTerm) => {
            console.log("🔍 Searching users:", searchTerm);
            await fetchUsers(searchTerm, 1, true);
        },
        [fetchUsers]
    );

    const debouncedSearch = useCallback(
        (searchTerm) => {
            // Clear previous timeout
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }

            // Set new timeout
            const timeoutId = setTimeout(() => {
                handleUsersSearch(searchTerm);
            }, 300);

            setSearchTimeout(timeoutId);
        },
        [searchTimeout, handleUsersSearch]
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const fetchGroupedPoints = useCallback(
        async (page = 1, showLoading = true) => {
            try {
                if (showLoading) {
                    setHistoryLoading(true);
                }

                // Fetch pagination info first
                const paginationInfo =
                    await LoyaltyAPI.getPointsHistoryPagination({
                        type:
                            filterType !== "All"
                                ? filterType === "Customer"
                                    ? "customer"
                                    : filterType === "Driver"
                                    ? "driver"
                                    : filterType.toLowerCase()
                                : undefined,
                        keyword: searchTerm,
                        page: page,
                        limit: pagination.per_page,
                        grouped: true,
                    });

                // Update pagination state
                setPagination(paginationInfo);

                // Fetch actual data
                const groupedData = await LoyaltyAPI.getGroupedPoints({
                    type:
                        filterType !== "All"
                            ? filterType === "Customer"
                                ? "customer"
                                : filterType === "Driver"
                                ? "driver"
                                : filterType.toLowerCase()
                            : undefined,
                    keyword: searchTerm,
                    page: page,
                    limit: pagination.per_page,
                    grouped: true,
                });

                setGroupedPoints(Array.isArray(groupedData) ? groupedData : []);
            } catch (err) {
                console.error("Error fetching grouped points:", err);
                setGroupedPoints([]);
                throw err;
            } finally {
                if (showLoading) {
                    setHistoryLoading(false);
                }
            }
        },
        [filterType, searchTerm, pagination.per_page]
    );

    // Fetch loyalty points configuration and stats from API
    useEffect(() => {
        const initializeData = async () => {
            try {
                setLoading(true);
                setAuthError(null);

                // Wait for authentication to be ready
                await waitForAuth();

                // Fetch all data in parallel with retry logic
                await Promise.all([
                    retryApiCall(fetchLoyaltyConfig),
                    retryApiCall(fetchLoyaltyStats),
                    retryApiCall(() => fetchGroupedPoints(1)),
                    retryApiCall(() => fetchUsers("", 1, false)),
                ]);
            } catch (error) {
                handleApiError(error, "initialize data");
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, []);

    // Fetch points history when filters change
    useEffect(() => {
        if (!isAuthenticated) return;

        const timeoutId = setTimeout(() => {
            // Reset pagination and data when filters change
            setPagination((prev) => ({ ...prev, current_page: 1 }));
            //
            retryApiCall(() => fetchGroupedPoints(1)).catch((error) => {
                handleApiError(error, "fetch grouped points");
            });
        }, 500); // Debounce search

        return () => clearTimeout(timeoutId);
    }, [
        searchTerm,
        filterType,
        isAuthenticated,
        fetchGroupedPoints,
        retryApiCall,
        handleApiError,
    ]);

    // Load more data for infinite scroll
    // Handle page change for pagination
    const handlePageChange = async (newPage) => {
        console.log("🔄 Changing to page:", newPage);

        // Clear table data first
        setGroupedPoints([]);
        setHistoryLoading(true);

        try {
            await fetchGroupedPoints(newPage, false); // Don't show loading again
        } catch (error) {
            console.error("❌ Error loading page:", error);
            // Keep loading state false even on error
        } finally {
            setHistoryLoading(false);
        }
    };

    // Handle scroll detection (removed infinite scroll)
    const handleScroll = (event) => {
        // Scroll detection removed - using pagination instead
    };

    // Helper function to get points from API config by key
    const getPointsByKey = (key) => {
        const config = apiConfig.find((item) => item.key === key);
        return config ? config.point : 0;
    };

    // Helper function to get config item by key
    const getConfigByKey = (key) => {
        return apiConfig.find((item) => item.key === key);
    };

    // Helper function to get minimum rating from API config
    const getMinimumRatingFromAPI = () => {
        const config = apiConfig.find((item) => item.key === "minimum_rating");
        return config ? parseFloat(config.point) : 4.0;
    };

    // Helper function to format date for input (avoiding timezone issues)
    const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Helper function to parse date from input (avoiding timezone issues)
    const parseDateFromInput = (dateString) => {
        const parts = dateString.split("-");
        return new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2])
        );
    };

    const handleSubmitPoints = async () => {
        try {
            setSubmittingPoints(true);
            const pointsValue = parseInt(pointsAmount);
            const finalPoints =
                pointsAction === "add" ? pointsValue : -pointsValue;

            await LoyaltyAPI.createPoints(selectedUser, finalPoints);

            // Reset form
            setSelectedUser("");
            setSelectedUserObject(null);
            setPointsAmount("");
            // Refresh stats and history after manual points operation
            await retryApiCall(fetchLoyaltyStats);
            setPagination((prev) => ({ ...prev, current_page: 1 }));
            //
            await retryApiCall(() => fetchGroupedPoints(1));

            // Show success notification
            setNotification({
                open: true,
                type: "success",
                message: `Points ${
                    pointsAction === "add" ? "added" : "subtracted"
                } successfully!`,
            });
        } catch (err) {
            console.error("Error updating points:", err);

            // Show error notification
            setNotification({
                open: true,
                type: "error",
                message: "Failed to update points. Please try again.",
            });

            // Still reset form even if API fails
            setSelectedUser("");
            setSelectedUserObject(null);
            setPointsAmount("");
        } finally {
            setSubmittingPoints(false);
        }
    };

    // Handle points modification in configuration
    const handlePointsChange = (key, newPoints) => {
        setModifiedActions((prev) => ({
            ...prev,
            [key]: parseInt(newPoints) || 0,
        }));
    };

    // Handle date range changes
    const handleDateRangeChange = (key, field, value) => {
        // Parse the date using our helper function to avoid timezone issues
        const parsedDate = parseDateFromInput(value);

        setModifiedDateRanges((prev) => {
            const newState = {
                ...prev,
                [key]: {
                    ...prev[key],
                    [field]: parsedDate,
                },
            };
            return newState;
        });
    };

    // Handle time range changes
    const handleTimeRangeChange = (key, field, value) => {
        setModifiedTimeRanges((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value,
            },
        }));
    };

    // Handle rating changes
    const handleRatingChange = (key, value) => {
        setModifiedRatings((prev) => ({
            ...prev,
            [key]: parseFloat(value) || 0,
        }));
    };

    // Handle save configuration
    const handleSaveConfiguration = async () => {
        try {
            setSaving(true);

            // Prepare data for API - build complete config data
            const allActions = [...userActions, ...driverActions];

            // Add minimum rating to the actions list
            const minimumRatingValue =
                modifiedRatings["high_rating"] !== undefined
                    ? modifiedRatings["high_rating"]
                    : getMinimumRatingFromAPI();
            const allActionsWithRating = [
                ...allActions,
                {
                    key: "minimum_rating",
                    points: minimumRatingValue,
                    hasDateRange: false,
                    hasTimeRange: false,
                },
            ];

            const requestBody = {
                key: allActionsWithRating.map((action) => action.key),
                point: allActionsWithRating.map((action) => action.points),
                type: allActionsWithRating.map((action) => {
                    if (action.key === "minimum_rating") return "system";
                    return userActions.includes(action) ? "customer" : "driver";
                }),
                time_start: allActionsWithRating.map((action) => {
                    if (action.hasTimeRange) {
                        const timeStart = action.startTime
                            .toTimeString()
                            .slice(0, 8);
                        return timeStart;
                    }
                    return null;
                }),
                time_end: allActionsWithRating.map((action) => {
                    if (action.hasTimeRange) {
                        const timeEnd = action.endTime
                            .toTimeString()
                            .slice(0, 8);
                        return timeEnd;
                    }
                    return null;
                }),
                date_start: allActionsWithRating.map((action) => {
                    if (action.hasDateRange) {
                        // Use local date string to avoid timezone issues
                        const year = action.startDate.getFullYear();
                        const month = String(
                            action.startDate.getMonth() + 1
                        ).padStart(2, "0");
                        const day = String(action.startDate.getDate()).padStart(
                            2,
                            "0"
                        );
                        const dateStart = `${year}-${month}-${day}T00:00:00.000000Z`;
                        return dateStart;
                    }
                    return null;
                }),
                date_end: allActionsWithRating.map((action) => {
                    if (action.hasDateRange) {
                        // Use local date string to avoid timezone issues
                        const year = action.endDate.getFullYear();
                        const month = String(
                            action.endDate.getMonth() + 1
                        ).padStart(2, "0");
                        const day = String(action.endDate.getDate()).padStart(
                            2,
                            "0"
                        );
                        const dateEnd = `${year}-${month}-${day}T23:59:59.000000Z`;
                        return dateEnd;
                    }
                    return null;
                }),
            };

            await LoyaltyAPI.updateLoyaltyPointsConfig(requestBody);

            // Clear modified actions
            setModifiedActions({});

            // Clear all modified states
            setModifiedDateRanges({});
            setModifiedTimeRanges({});
            setModifiedRatings({});

            // Refresh config data
            await retryApiCall(fetchLoyaltyConfig);

            // Close dialog
            setConfigOpen(false);

            // Show success notification
            setNotification({
                open: true,
                type: "success",
                message: "Points configuration updated successfully!",
            });
        } catch (err) {
            console.error("Error saving configuration:", err);

            // Show error notification
            setNotification({
                open: true,
                type: "error",
                message:
                    "Failed to update points configuration. Please try again.",
            });
        } finally {
            setSaving(false);
        }
    };

    // Reset configuration state when dialog opens
    const handleConfigOpen = () => {
        // Clear all modified states to show fresh data from API
        setModifiedActions({});
        setModifiedDateRanges({});
        setModifiedTimeRanges({});
        setModifiedRatings({});
        setConfigOpen(true);
    };

    // User actions with API data integration
    const userActions = [
        {
            key: "registration_bonus",
            name: t("loyalty_points_action_registration_bonus"),
            points:
                modifiedActions["registration_bonus"] !== undefined
                    ? modifiedActions["registration_bonus"]
                    : getPointsByKey("registration_bonus"),
            description: t("loyalty_points_description_granted"),
        },
        {
            key: "take_trip",
            name: t("loyalty_points_action_take_ride"),
            points:
                modifiedActions["take_trip"] !== undefined
                    ? modifiedActions["take_trip"]
                    : getPointsByKey("take_trip"),
            description: t("loyalty_points_description_granted"),
        },
        {
            key: "payment_with_card",
            name: t("loyalty_points_action_pay_card"),
            points:
                modifiedActions["payment_with_card"] !== undefined
                    ? modifiedActions["payment_with_card"]
                    : getPointsByKey("payment_with_card"),
            description: t("loyalty_points_description_granted"),
        },
        {
            key: "payment_with_cash",
            name: t("loyalty_points_action_pay_cash"),
            points:
                modifiedActions["payment_with_cash"] !== undefined
                    ? modifiedActions["payment_with_cash"]
                    : getPointsByKey("payment_with_cash"),
            description: t("loyalty_points_description_granted"),
        },
        {
            key: "trip_in_date_range",
            name: t("loyalty_points_action_specific_day"),
            points:
                modifiedActions["trip_in_date_range"] !== undefined
                    ? modifiedActions["trip_in_date_range"]
                    : getPointsByKey("trip_in_date_range"),
            description: t("loyalty_points_description_granted"),
            hasDateRange: true,
            startDate: (() => {
                const modifiedStart =
                    modifiedDateRanges["trip_in_date_range"]?.startDate;
                const apiStart =
                    getConfigByKey("trip_in_date_range")?.date_start;
                const defaultStart = "2024-01-01";

                let result;
                if (modifiedStart) {
                    // Use the modified date directly (it's already a Date object)
                    result = modifiedStart;
                } else if (apiStart) {
                    result = new Date(apiStart);
                } else {
                    result = parseDateFromInput(defaultStart);
                }
                return result;
            })(),
            endDate: (() => {
                const modifiedEnd =
                    modifiedDateRanges["trip_in_date_range"]?.endDate;
                const apiEnd = getConfigByKey("trip_in_date_range")?.date_end;
                const defaultEnd = "2024-12-31";

                let result;
                if (modifiedEnd) {
                    // Use the modified date directly (it's already a Date object)
                    result = modifiedEnd;
                } else if (apiEnd) {
                    result = new Date(apiEnd);
                } else {
                    result = parseDateFromInput(defaultEnd);
                }
                return result;
            })(),
        },
        {
            key: "trip_in_time_range",
            name: t("loyalty_points_action_specific_time"),
            points:
                modifiedActions["trip_in_time_range"] !== undefined
                    ? modifiedActions["trip_in_time_range"]
                    : getPointsByKey("trip_in_time_range"),
            description: t("loyalty_points_description_granted"),
            hasTimeRange: true,
            startTime: modifiedTimeRanges["trip_in_time_range"]?.startTime
                ? new Date(
                      `2024-01-01T${modifiedTimeRanges["trip_in_time_range"].startTime}`
                  )
                : getConfigByKey("trip_in_time_range")?.time_start
                ? new Date(
                      `2024-01-01T${
                          getConfigByKey("trip_in_time_range").time_start
                      }`
                  )
                : new Date(2024, 0, 1, 6, 0),
            endTime: modifiedTimeRanges["trip_in_time_range"]?.endTime
                ? new Date(
                      `2024-01-01T${modifiedTimeRanges["trip_in_time_range"].endTime}`
                  )
                : getConfigByKey("trip_in_time_range")?.time_end
                ? new Date(
                      `2024-01-01T${
                          getConfigByKey("trip_in_time_range").time_end
                      }`
                  )
                : new Date(2024, 0, 1, 22, 0),
        },
    ];

    // Driver actions with API data integration
    const driverActions = [
        {
            key: "accept_trip",
            name: t("loyalty_points_action_accept_ride"),
            points:
                modifiedActions["accept_trip"] !== undefined
                    ? modifiedActions["accept_trip"]
                    : getPointsByKey("accept_trip"),
            description: t("loyalty_points_description_granted"),
        },
        {
            key: "high_rating",
            name: t("loyalty_points_action_high_rating"),
            points:
                modifiedActions["high_rating"] !== undefined
                    ? modifiedActions["high_rating"]
                    : getPointsByKey("high_rating"),
            description: t("loyalty_points_description_granted"),
            hasRating: true,
            minRating:
                modifiedRatings["high_rating"] !== undefined
                    ? modifiedRatings["high_rating"]
                    : getMinimumRatingFromAPI(),
        },
        {
            key: "accept_in_specific_hours",
            name: t("loyalty_points_action_accept_specific_time"),
            points:
                modifiedActions["accept_in_specific_hours"] !== undefined
                    ? modifiedActions["accept_in_specific_hours"]
                    : getPointsByKey("accept_in_specific_hours"),
            description: t("loyalty_points_description_granted"),
            hasTimeRange: true,
            startTime: modifiedTimeRanges["accept_in_specific_hours"]?.startTime
                ? new Date(
                      `2024-01-01T${modifiedTimeRanges["accept_in_specific_hours"].startTime}`
                  )
                : getConfigByKey("accept_in_specific_hours")?.time_start
                ? new Date(
                      `2024-01-01T${
                          getConfigByKey("accept_in_specific_hours").time_start
                      }`
                  )
                : new Date(2024, 0, 1, 6, 0),
            endTime: modifiedTimeRanges["accept_in_specific_hours"]?.endTime
                ? new Date(
                      `2024-01-01T${modifiedTimeRanges["accept_in_specific_hours"].endTime}`
                  )
                : getConfigByKey("accept_in_specific_hours")?.time_end
                ? new Date(
                      `2024-01-01T${
                          getConfigByKey("accept_in_specific_hours").time_end
                      }`
                  )
                : new Date(2024, 0, 1, 22, 0),
        },
        {
            key: "cancel_after_accept",
            name: t("loyalty_points_action_cancel_after_accept"),
            points:
                modifiedActions["cancel_after_accept"] !== undefined
                    ? modifiedActions["cancel_after_accept"]
                    : getPointsByKey("cancel_after_accept"),
            description: t("loyalty_points_description_deducted"),
            negative: true,
        },
    ];

    // Fetch grouped points data

    // Fetch user history details
    const fetchUserHistory = async (userId, page = 1, freshSearch = false) => {
        try {
            setUserHistoryLoading(true);

            // Clear data first when changing page
            if (freshSearch || page !== userHistoryPagination.current_page) {
                setUserHistory([]);
            }

            // Fetch pagination info first
            console.log("📡 Fetching pagination info with params:", {
                userId: userId,
                page: page,
                limit: userHistoryPagination.per_page,
                grouped: false,
            });

            const paginationInfo = await LoyaltyAPI.getPointsHistoryPagination({
                userId: userId,
                page: page,
                limit: userHistoryPagination.per_page,
                grouped: false, // Get individual records, not grouped
            });

            // Update pagination state
            console.log("📊 Setting user history pagination:", paginationInfo);
            setUserHistoryPagination(paginationInfo);

            // Fetch actual data
            const historyData = await LoyaltyAPI.getPointsHistory({
                userId: userId,
                page: page,
                limit: userHistoryPagination.per_page,
                grouped: false,
            });

            setUserHistory(Array.isArray(historyData) ? historyData : []);
        } catch (err) {
            console.error("❌ Error fetching user history:", err);
            console.error("❌ Error details:", err.message, err.response?.data);
            setUserHistory([]);
            throw err;
        } finally {
            setUserHistoryLoading(false);
        }
    };

    // Handle user history page change
    const handleUserHistoryPageChange = async (newPage) => {
        console.log("🔄 Changing user history to page:", newPage);

        // Clear table data first
        setUserHistory([]);
        setUserHistoryLoading(true);

        try {
            if (selectedUserDetail) {
                const userIdToFetch =
                    selectedUserDetail.user_id ||
                    selectedUserDetail.userId ||
                    selectedUserDetail.id;
                await fetchUserHistory(userIdToFetch, newPage, false);
            }
        } catch (error) {
            console.error("❌ Error loading user history page:", error);
            // Keep loading state false even on error
        } finally {
            setUserHistoryLoading(false);
        }
    };

    // Handle view user details
    const handleViewUserDetails = (user) => {
        setSelectedUserDetail(user);
        setDetailModalOpen(true);
        setUserHistory([]);
        setUserHistoryPagination({
            current_page: 1,
            per_page: 10,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false,
        });
        // Use the user_id from the grouped data to fetch detailed history
        const userIdToFetch = user.user_id || user.userId || user.id;
        fetchUserHistory(userIdToFetch, 1, true);
    };

    if (loading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "400px",
                    flexDirection: "column",
                }}
            >
                <CircularProgress />
                <Typography variant="body2" style={{ marginTop: "16px" }}>
                    {authError ? t("authentication_required") : t("loading")}
                </Typography>
                {retryCount > 0 && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: "8px" }}
                    >
                        {t("retry_attempt")} {retryCount} {t("of")} {maxRetries}
                    </Typography>
                )}
            </div>
        );
    }

    if (authError) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "400px",
                    flexDirection: "column",
                }}
            >
                <Typography
                    variant="h6"
                    color="error"
                    style={{ marginBottom: "16px" }}
                >
                    {t("authentication_error")}
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: "16px", textAlign: "center" }}
                >
                    {authError}
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                    style={{
                        backgroundColor: colors.MAIN_COLOR,
                        color: "white",
                    }}
                >
                    {t("refresh_page")}
                </Button>
            </div>
        );
    }

    const getTranslationForHistoryPointsDescription = (description) => {
        // Handle undefined or null description
        if (!description) {
            return "";
        }
        
        let modifiedDescription = description;

        if (description.includes("Refund for cancelled booking")) {
            const refundCancelledBooking = t("refund_cancelled_booking");
            modifiedDescription = modifiedDescription.replace(
                "Refund for cancelled booking",
                refundCancelledBooking
            );
        }

        if (description.includes("Trip cost")) {
            modifiedDescription = modifiedDescription.replace(
                "Trip cost",
                t("trip_cost")
            );
        }

        if (description.includes("Points deduction")) {
            modifiedDescription = modifiedDescription.replace(
                "Points deduction",
                t("points_deduction")
            );
        }

        if (description.includes("Points addition")) {
            modifiedDescription = modifiedDescription.replace(
                "Points addition",
                t("Points addition")
            );
        }

        if (description.includes("Payment for booking")) {
            const paymentForBooking = t("payment_for_booking");
            modifiedDescription = modifiedDescription.replace(
                "Payment for booking",
                paymentForBooking
            );
        }

        if (modifiedDescription.includes("Manual points assignment")) {
            modifiedDescription = t("manual_points_assignment");
        }

        if (modifiedDescription.includes("Manual points deduction")) {
            modifiedDescription = t("manual_points_deduction");
        }

        if (
            modifiedDescription.includes("Points earned for completing a trip")
        ) {
            modifiedDescription = t("points_earned_for_completing_a_trip");
        }

        if (
            modifiedDescription.includes("Points earned for accepting a trip")
        ) {
            modifiedDescription = t("points_earned_for_accepting_a_trip");
        }

        if (
            modifiedDescription.includes(
                "Bonus points for maintaining high rating"
            )
        ) {
            modifiedDescription = t("bonus_points_for_maintaining_high_rating");
        }

        if (
            modifiedDescription.includes(
                "Point penalty for canceling after accepting trip"
            )
        ) {
            modifiedDescription = t(
                "point_penalty_for_canceling_after_accepting_trip"
            );
        }

        if (
            modifiedDescription.includes("Points for using card payment method")
        ) {
            modifiedDescription = t("points_for_using_card_payment_method");
        }

        if (
            modifiedDescription.includes("Points for using cash payment method")
        ) {
            modifiedDescription = t("points_for_using_cash_payment_method");
        }

        if (
            modifiedDescription.includes(
                "Points for using yappi payment method"
            )
        ) {
            modifiedDescription = t("points_for_using_yappi_payment_method");
        }

        if (
            modifiedDescription.includes(
                "Points for using bank transfer payment method"
            )
        ) {
            modifiedDescription = t(
                "points_for_using_bank_transfer_payment_method"
            );
        }

        if (
            modifiedDescription.includes(
                "Points for using paypal payment method"
            )
        ) {
            modifiedDescription = t("points_for_using_paypal_payment_method");
        }

        if (
            modifiedDescription.includes(
                "Points for using wallet payment method"
            )
        ) {
            modifiedDescription = t("points_for_using_wallet_payment_method");
        }

        if (
            modifiedDescription.includes(
                "Points for using points payment method"
            )
        ) {
            modifiedDescription = t("points_for_using_points_payment_method");
        }

        return modifiedDescription;
    };

    return (
        <div style={{ padding: "20px" }}>
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                }}
            >
                <div>
                    <Typography
                        variant="h4"
                        style={{ fontWeight: "bold", marginBottom: "5px" }}
                    >
                        {t("loyalty_points_page")}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                        {t("loyalty_points_manage_subtitle")}
                    </Typography>
                </div>
                <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={handleConfigOpen}
                    style={{
                        borderColor: colors.MAIN_COLOR,
                        color: colors.MAIN_COLOR,
                    }}
                >
                    {t("loyalty_points_configure")}
                </Button>
            </div>

            {/* Stats Cards */}
            <Grid container spacing={3} style={{ marginBottom: "30px" }}>
                <Grid item xs={12} md={4}>
                    <Card
                        style={{
                            borderRadius: "16px",
                            padding: "24px",
                            background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div>
                                <Typography
                                    variant="h3"
                                    style={{
                                        fontWeight: "bold",
                                        marginBottom: "8px",
                                    }}
                                >
                                    {totalCustomers}
                                </Typography>
                                <Typography variant="h6">
                                    {t("loyalty_points_total_customers")}
                                </Typography>
                            </div>
                            <GroupIcon
                                style={{ fontSize: "48px", opacity: 0.8 }}
                            />
                        </div>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        style={{
                            borderRadius: "16px",
                            padding: "24px",
                            background:
                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            color: "white",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div>
                                <Typography
                                    variant="h3"
                                    style={{
                                        fontWeight: "bold",
                                        marginBottom: "8px",
                                    }}
                                >
                                    {totalDrivers}
                                </Typography>
                                <Typography variant="h6">
                                    {t("loyalty_points_total_drivers")}
                                </Typography>
                            </div>
                            <CarIcon
                                style={{ fontSize: "48px", opacity: 0.8 }}
                            />
                        </div>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        style={{
                            borderRadius: "16px",
                            padding: "24px",
                            background:
                                "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                            color: "white",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div>
                                <Typography
                                    variant="h3"
                                    style={{
                                        fontWeight: "bold",
                                        marginBottom: "8px",
                                    }}
                                >
                                    {totalPoints.toLocaleString()}
                                </Typography>
                                <Typography variant="h6">
                                    {t("loyalty_points_total_points")}
                                </Typography>
                            </div>
                            <StarIcon
                                style={{ fontSize: "48px", opacity: 0.8 }}
                            />
                        </div>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Points Table */}
                <Grid item xs={12} lg={8}>
                    <Card style={{ borderRadius: "16px", padding: "24px" }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                            }}
                        >
                            <Typography
                                variant="h5"
                                style={{ fontWeight: "bold" }}
                            >
                                {t("loyalty_points_accumulated_points")}
                            </Typography>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "16px",
                                    alignItems: "center",
                                }}
                            >
                                <TextField
                                    size="small"
                                    placeholder={t(
                                        "loyalty_points_search_placeholder"
                                    )}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setGroupedPoints([]);
                                        setPagination((prev) => ({
                                            ...prev,
                                            current_page: 1,
                                        }));
                                        setSearchTerm(e.target.value);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{ width: "300px" }}
                                />
                                <FormControl
                                    size="small"
                                    style={{ minWidth: "120px" }}
                                >
                                    <Select
                                        value={filterType}
                                        onChange={(e) => {
                                            // Clear table results when filter changes
                                            setGroupedPoints([]);
                                            setPagination((prev) => ({
                                                ...prev,
                                                current_page: 1,
                                            }));
                                            setFilterType(e.target.value);
                                        }}
                                    >
                                        <MenuItem value="All">
                                            {t("loyalty_points_filter_all")}
                                        </MenuItem>
                                        <MenuItem value="Customer">
                                            {t(
                                                "loyalty_points_filter_customer"
                                            )}
                                        </MenuItem>
                                        <MenuItem value="Driver">
                                            {t("loyalty_points_filter_driver")}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </div>

                        <TableContainer
                            key={filterType}
                            onScroll={handleScroll}
                            style={{ maxHeight: "500px", overflowY: "auto" }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <strong>
                                                {t("loyalty_points_table_name")}
                                            </strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>
                                                {t(
                                                    "loyalty_points_table_email"
                                                )}
                                            </strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>
                                                {t("loyalty_points_table_type")}
                                            </strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>
                                                {t(
                                                    "loyalty_points_table_total_points"
                                                )}
                                            </strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>
                                                {t(
                                                    "loyalty_points_table_transactions"
                                                )}
                                            </strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>
                                                {t(
                                                    "loyalty_points_table_last_activity"
                                                )}
                                            </strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>
                                                {t(
                                                    "loyalty_points_table_actions"
                                                )}
                                            </strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupedPoints.length === 0 &&
                                    !historyLoading ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                style={{
                                                    textAlign: "center",
                                                    padding: "40px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {t(
                                                        "loyalty_points_no_data"
                                                    )}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        <>
                                            {groupedPoints.map((user) => (
                                                <TableRow
                                                    key={user.email}
                                                    hover
                                                >
                                                    <TableCell>
                                                        {user.userName || "N/A"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.email || "N/A"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={
                                                                user.type ===
                                                                "customer"
                                                                    ? t(
                                                                          "loyalty_points_filter_customer"
                                                                      )
                                                                    : user.type ===
                                                                      "driver"
                                                                    ? t(
                                                                          "loyalty_points_filter_driver"
                                                                      )
                                                                    : user.type
                                                            }
                                                            color={
                                                                user.type ===
                                                                "driver"
                                                                    ? "primary"
                                                                    : "default"
                                                            }
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: "4px",
                                                            }}
                                                        >
                                                            <StarIcon
                                                                style={{
                                                                    color: "#FFD700",
                                                                    fontSize:
                                                                        "16px",
                                                                }}
                                                            />
                                                            <strong>
                                                                {user.pointTotal ||
                                                                    user.totalPoint ||
                                                                    0}
                                                            </strong>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={`${
                                                                user.count ||
                                                                user.transactionCount ||
                                                                0
                                                            } ${t(
                                                                "loyalty_points_table_transactions"
                                                            ).toLowerCase()}`}
                                                            color="info"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.lastActivity
                                                            ? new Date(
                                                                  user.lastActivity
                                                              ).toLocaleDateString()
                                                            : "N/A"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() =>
                                                                handleViewUserDetails(
                                                                    user
                                                                )
                                                            }
                                                            style={{
                                                                borderColor:
                                                                    colors.MAIN_COLOR,
                                                                color: colors.MAIN_COLOR,
                                                                fontSize:
                                                                    "12px",
                                                            }}
                                                        >
                                                            {t(
                                                                "loyalty_points_view_details"
                                                            )}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {historyLoading && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={7}
                                                        style={{
                                                            textAlign: "center",
                                                            padding: "20px",
                                                        }}
                                                    >
                                                        <CircularProgress
                                                            size={24}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            style={{
                                                                marginTop:
                                                                    "8px",
                                                            }}
                                                        >
                                                            {t("loading")}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    marginTop: "20px",
                                    gap: "16px",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {t("show")}{" "}
                                    {(pagination.current_page - 1) *
                                        pagination.per_page +
                                        1}{" "}
                                    {t("to")}{" "}
                                    {Math.min(
                                        pagination.current_page *
                                            pagination.per_page,
                                        pagination.total
                                    )}{" "}
                                    {t("of")} {pagination.total}
                                </Typography>
                                <Pagination
                                    count={pagination.total_pages}
                                    page={pagination.current_page}
                                    onChange={(event, page) =>
                                        handlePageChange(page)
                                    }
                                    color="primary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                    disabled={historyLoading}
                                />
                            </div>
                        )}
                    </Card>
                </Grid>

                {/* Manual Points Management */}
                <Grid item xs={12} lg={4}>
                    <Card style={{ borderRadius: "16px", padding: "24px" }}>
                        <Typography
                            variant="h5"
                            style={{ fontWeight: "bold", marginBottom: "20px" }}
                        >
                            {t("loyalty_points_manual_management")}
                        </Typography>

                        <div style={{ marginBottom: "20px" }}>
                            <Typography
                                variant="subtitle2"
                                style={{
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                }}
                            >
                                {t("loyalty_points_select_user")}
                            </Typography>
                            <Autocomplete
                                ref={autocompleteInputRef}
                                value={selectedUserObject}
                                inputValue={autocompleteInputValue}
                                options={usersList}
                                getOptionLabel={(option) => {
                                    if (!option) return "";
                                    return `${
                                        option.name || option.email || "No Name"
                                    } (${
                                        option.type === "customer"
                                            ? t(
                                                  "loyalty_points_filter_customer"
                                              )
                                            : option.type === "driver"
                                            ? t("loyalty_points_filter_driver")
                                            : option.type
                                    })`;
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    if (!option || !value) return false;
                                    return option.id === value.id;
                                }}
                                loading={usersLoading}
                                open={autocompleteOpen}
                                freeSolo={false}
                                selectOnFocus={true}
                                clearOnBlur={false}
                                handleHomeEndKeys={true}
                                onOpen={() => {
                                    console.log("🔓 Autocomplete opened");
                                    setAutocompleteOpen(true);
                                    // Load initial data when autocomplete opens if no data
                                    if (
                                        usersList.length === 0 &&
                                        !usersLoading
                                    ) {
                                        console.log(
                                            "🔓 Loading initial users..."
                                        );
                                        handleUsersSearch("");
                                    }
                                }}
                                onClose={(event, reason) => {
                                    console.log(
                                        "🔒 Autocomplete close requested, reason:",
                                        reason
                                    );
                                    // Close autocomplete for all valid reasons
                                    if (
                                        reason === "selectOption" ||
                                        reason === "blur" ||
                                        reason === "escape"
                                    ) {
                                        setAutocompleteOpen(false);
                                    }
                                }}
                                onChange={(event, newValue) => {
                                    console.log(
                                        "🔄 User selection changed:",
                                        newValue
                                    );
                                    setSelectedUserObject(newValue);
                                    setSelectedUser(
                                        newValue ? newValue.id : ""
                                    );

                                    if (newValue) {
                                        // Update input value to match selected user
                                        setAutocompleteInputValue(
                                            `${
                                                newValue.name ||
                                                newValue.email ||
                                                "No Name"
                                            } (${
                                                newValue.type === "customer"
                                                    ? t(
                                                          "loyalty_points_filter_customer"
                                                      )
                                                    : newValue.type === "driver"
                                                    ? t(
                                                          "loyalty_points_filter_driver"
                                                      )
                                                    : newValue.type
                                            })`
                                        );
                                        setAutocompleteOpen(false); // Close after selection
                                    } else {
                                        // Clear input when no user selected
                                        setAutocompleteInputValue("");
                                    }
                                }}
                                onInputChange={(event, value, reason) => {
                                    console.log(
                                        "🔤 Input changed:",
                                        value,
                                        "reason:",
                                        reason
                                    );

                                    // Only update input value if it's actually different to prevent cursor reset
                                    if (autocompleteInputValue !== value) {
                                        setAutocompleteInputValue(value);
                                    }

                                    // Only search if user is typing (not when selecting from dropdown)
                                    if (reason === "input") {
                                        // Clear selection if user starts typing
                                        if (selectedUserObject) {
                                            setSelectedUserObject(null);
                                            setSelectedUser("");
                                        }
                                        // Open autocomplete when user starts typing
                                        if (!autocompleteOpen) {
                                            setAutocompleteOpen(true);
                                        }
                                        debouncedSearch(value);
                                    } else if (reason === "clear") {
                                        // Handle clear button
                                        setSelectedUserObject(null);
                                        setSelectedUser("");
                                        setAutocompleteInputValue("");
                                        debouncedSearch("");
                                    }

                                    // if (reason === 'input') {
                                    //   // Open autocomplete when user starts typing
                                    //   if (!autocompleteOpen) {
                                    //     setAutocompleteOpen(true);
                                    //   }
                                    //   debouncedSearch(value);

                                    //   // Maintain focus and cursor position after search
                                    //   setTimeout(() => {
                                    //     const inputElement = autocompleteInputRef.current?.querySelector('input');
                                    //     if (inputElement) {
                                    //       inputElement.focus();
                                    //       // Set cursor to end of text
                                    //       const textLength = inputElement.value.length;
                                    //       // inputElement.setSelectionRange(textLength, textLength);
                                    //     }
                                    //   }, 150);
                                    // } else if (reason === 'clear') {
                                    //   // Handle clear button - only clear when explicitly clearing
                                    //   setAutocompleteInputValue('');
                                    //   setAutocompleteOpen(true); // Keep open after clear
                                    //   debouncedSearch('');
                                    // }
                                }}
                                ListboxProps={{
                                    onScroll: (event) => {
                                        const {
                                            scrollTop,
                                            scrollHeight,
                                            clientHeight,
                                        } = event.target;
                                        const isNearBottom =
                                            scrollTop + clientHeight >=
                                            scrollHeight - 100;

                                        if (
                                            isNearBottom &&
                                            usersPagination.hasMoreData &&
                                            !usersLoadingMore
                                        ) {
                                            console.log(
                                                "📜 Scrolled to bottom, loading more users..."
                                            );
                                            loadMoreUsers();
                                        }
                                    },
                                }}
                                renderOption={(props, option, { index }) => (
                                    <>
                                        <Box
                                            component="li"
                                            {...props}
                                            key={option.id}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "center",
                                                    width: "100%",
                                                }}
                                            >
                                                <div>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {option.name ||
                                                            option.email ||
                                                            "No Name"}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {option.email}
                                                    </Typography>
                                                </div>
                                                <Chip
                                                    label={
                                                        option.type ===
                                                        "customer"
                                                            ? t(
                                                                  "loyalty_points_filter_customer"
                                                              )
                                                            : option.type ===
                                                              "driver"
                                                            ? t(
                                                                  "loyalty_points_filter_driver"
                                                              )
                                                            : option.type
                                                    }
                                                    color={
                                                        option.type === "driver"
                                                            ? "primary"
                                                            : "default"
                                                    }
                                                    size="small"
                                                />
                                            </div>
                                        </Box>
                                        {/* Show loading indicator at the end of list when loading more */}
                                        {index === usersList.length - 1 &&
                                            usersLoadingMore && (
                                                <Box
                                                    component="li"
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "center",
                                                        p: 1,
                                                        width: "100%",
                                                    }}
                                                >
                                                    <CircularProgress
                                                        size={20}
                                                    />
                                                </Box>
                                            )}
                                    </>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={
                                            usersLoading
                                                ? t(
                                                      "loyalty_points_loading_users"
                                                  )
                                                : usersList.length === 0
                                                ? t("loyalty_points_no_users")
                                                : t(
                                                      "loyalty_points_search_users"
                                                  )
                                        }
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {usersLoading ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={20}
                                                        />
                                                    ) : null}
                                                    {
                                                        params.InputProps
                                                            .endAdornment
                                                    }
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                noOptionsText={
                                    usersLoading
                                        ? t("loyalty_points_loading_users")
                                        : usersList.length === 0
                                        ? t("loyalty_points_no_users_found")
                                        : t("loyalty_points_no_matching_users")
                                }
                                loadingText={t("loyalty_points_loading_users")}
                                // disabled={usersLoading}
                                filterOptions={(options) => {
                                    // Don't filter options since we're doing server-side filtering
                                    return options;
                                }}
                                sx={{ width: "100%" }}
                            />
                        </div>

                        <div style={{ marginBottom: "20px" }}>
                            <Typography
                                variant="subtitle2"
                                style={{
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                }}
                            >
                                {t("loyalty_points_action_label")}
                            </Typography>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <Button
                                    variant={
                                        pointsAction === "add"
                                            ? "contained"
                                            : "outlined"
                                    }
                                    startIcon={<AddIcon />}
                                    onClick={() => setPointsAction("add")}
                                    style={{
                                        flex: 1,
                                        backgroundColor:
                                            pointsAction === "add"
                                                ? colors.MAIN_COLOR
                                                : "transparent",
                                        borderColor: colors.MAIN_COLOR,
                                        color:
                                            pointsAction === "add"
                                                ? "white"
                                                : colors.MAIN_COLOR,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {t("loyalty_points_add")}
                                </Button>
                                <Button
                                    variant={
                                        pointsAction === "subtract"
                                            ? "contained"
                                            : "outlined"
                                    }
                                    startIcon={<RemoveIcon />}
                                    onClick={() => setPointsAction("subtract")}
                                    style={{
                                        flex: 1,
                                        backgroundColor:
                                            pointsAction === "subtract"
                                                ? "#f44336"
                                                : "transparent",
                                        borderColor: "#f44336",
                                        color:
                                            pointsAction === "subtract"
                                                ? "white"
                                                : "#f44336",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {t("loyalty_points_subtract")}
                                </Button>
                            </div>
                        </div>

                        <div style={{ marginBottom: "20px" }}>
                            <Typography
                                variant="subtitle2"
                                style={{
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                }}
                            >
                                {t("loyalty_points_amount_label")}
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={t("loyalty_points_enter_amount")}
                                value={pointsAmount}
                                onChange={(e) =>
                                    setPointsAmount(e.target.value)
                                }
                                type="number"
                            />
                        </div>

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={
                                selectedUserObject &&
                                pointsAmount &&
                                !submittingPoints
                                    ? handleSubmitPoints
                                    : undefined
                            }
                            disabled={
                                !selectedUserObject ||
                                !pointsAmount ||
                                submittingPoints
                            }
                            style={{
                                backgroundColor:
                                    pointsAction === "add"
                                        ? "#4caf50"
                                        : "#f44336",
                                color: "white",
                                padding: "12px",
                                fontSize: "16px",
                                fontWeight: "bold",
                            }}
                            startIcon={
                                submittingPoints ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : null
                            }
                        >
                            {submittingPoints
                                ? t("loyalty_points_processing")
                                : pointsAction === "add"
                                ? t("loyalty_points_add_points")
                                : t("loyalty_points_subtract_points")}
                        </Button>
                    </Card>
                </Grid>
            </Grid>

            {/* Configuration Modal */}
            <Dialog
                open={configOpen}
                onClose={() => setConfigOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h5" style={{ fontWeight: "bold" }}>
                        {t("loyalty_points_configuration")}
                    </Typography>
                    <IconButton onClick={() => setConfigOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Tabs
                        value={configTab}
                        onChange={(e, newValue) => setConfigTab(newValue)}
                    >
                        <Tab
                            label={t("loyalty_points_user_actions")}
                            icon={<GroupIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label={t("loyalty_points_driver_actions")}
                            icon={<CarIcon />}
                            iconPosition="start"
                        />
                    </Tabs>

                    <TabPanel value={configTab} index={0}>
                        <div style={{ marginTop: "16px" }}>
                            {userActions.map((action, index) => (
                                <Card
                                    key={index}
                                    style={{
                                        marginBottom: "16px",
                                        borderRadius: "8px",
                                        borderLeft: `4px solid ${colors.MAIN_COLOR}`,
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    }}
                                >
                                    <div style={{ padding: "20px" }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    style={{
                                                        fontWeight: "bold",
                                                        marginBottom: "4px",
                                                    }}
                                                >
                                                    {action.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{
                                                        marginBottom: "12px",
                                                    }}
                                                >
                                                    {action.description}
                                                </Typography>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "12px",
                                                }}
                                            >
                                                <div>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: "bold",
                                                            marginBottom: "4px",
                                                        }}
                                                    >
                                                        {t(
                                                            "loyalty_points_points_label"
                                                        )}
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        value={action.points}
                                                        onChange={(e) =>
                                                            handlePointsChange(
                                                                action.key,
                                                                e.target.value
                                                            )
                                                        }
                                                        type="number"
                                                        style={{
                                                            width: "80px",
                                                        }}
                                                        disabled={saving}
                                                        InputProps={{
                                                            style: {
                                                                textAlign:
                                                                    "center",
                                                                fontWeight:
                                                                    "bold",
                                                            },
                                                        }}
                                                    />
                                                </div>
                                                <Chip
                                                    label={`+${action.points}`}
                                                    style={{
                                                        backgroundColor:
                                                            "#e8f5e8",
                                                        color: "#2e7d32",
                                                        fontWeight: "bold",
                                                    }}
                                                    size="small"
                                                />
                                            </div>
                                        </div>

                                        {action.hasDateRange && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "16px",
                                                    marginTop: "12px",
                                                    padding: "12px",
                                                    backgroundColor: "#f5f5f5",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: "bold",
                                                        minWidth: "80px",
                                                    }}
                                                >
                                                    {t(
                                                        "loyalty_points_date_range"
                                                    )}
                                                </Typography>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    <TextField
                                                        size="small"
                                                        label={t(
                                                            "loyalty_points_start_date"
                                                        )}
                                                        type="date"
                                                        value={formatDateForInput(
                                                            action.startDate
                                                        )}
                                                        onChange={(e) =>
                                                            handleDateRangeChange(
                                                                action.key,
                                                                "startDate",
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width: "140px",
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                    <Typography>
                                                        {t("loyalty_points_to")}
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        label={t(
                                                            "loyalty_points_end_date"
                                                        )}
                                                        type="date"
                                                        value={formatDateForInput(
                                                            action.endDate
                                                        )}
                                                        onChange={(e) =>
                                                            handleDateRangeChange(
                                                                action.key,
                                                                "endDate",
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width: "140px",
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {action.hasTimeRange && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "16px",
                                                    marginTop: "12px",
                                                    padding: "12px",
                                                    backgroundColor: "#f5f5f5",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: "bold",
                                                        minWidth: "80px",
                                                    }}
                                                >
                                                    {t(
                                                        "loyalty_points_time_range"
                                                    )}
                                                </Typography>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    <TextField
                                                        size="small"
                                                        label={t(
                                                            "loyalty_points_start_time"
                                                        )}
                                                        type="time"
                                                        value={action.startTime
                                                            .toTimeString()
                                                            .slice(0, 5)}
                                                        onChange={(e) =>
                                                            handleTimeRangeChange(
                                                                action.key,
                                                                "startTime",
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width: "120px",
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                    <Typography>
                                                        {t("loyalty_points_to")}
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        label={t(
                                                            "loyalty_points_end_time"
                                                        )}
                                                        type="time"
                                                        value={action.endTime
                                                            .toTimeString()
                                                            .slice(0, 5)}
                                                        onChange={(e) =>
                                                            handleTimeRangeChange(
                                                                action.key,
                                                                "endTime",
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width: "120px",
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </TabPanel>

                    <TabPanel value={configTab} index={1}>
                        <div style={{ marginTop: "16px" }}>
                            {driverActions.map((action, index) => (
                                <Card
                                    key={index}
                                    style={{
                                        marginBottom: "16px",
                                        borderRadius: "8px",
                                        borderLeft: `4px solid ${
                                            action.negative
                                                ? "#f44336"
                                                : "#4caf50"
                                        }`,
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                        backgroundColor: action.negative
                                            ? "#fff5f5"
                                            : "white",
                                    }}
                                >
                                    <div style={{ padding: "20px" }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    style={{
                                                        fontWeight: "bold",
                                                        marginBottom: "4px",
                                                    }}
                                                >
                                                    {action.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{
                                                        marginBottom: "12px",
                                                    }}
                                                >
                                                    {action.description}
                                                </Typography>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "12px",
                                                }}
                                            >
                                                <div>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: "bold",
                                                            marginBottom: "4px",
                                                        }}
                                                    >
                                                        {t(
                                                            "loyalty_points_points_label"
                                                        )}
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        value={Math.abs(
                                                            action.points
                                                        )}
                                                        onChange={(e) =>
                                                            handlePointsChange(
                                                                action.key,
                                                                action.negative
                                                                    ? -Math.abs(
                                                                          e
                                                                              .target
                                                                              .value
                                                                      )
                                                                    : Math.abs(
                                                                          e
                                                                              .target
                                                                              .value
                                                                      )
                                                            )
                                                        }
                                                        type="number"
                                                        style={{
                                                            width: "80px",
                                                        }}
                                                        disabled={saving}
                                                        InputProps={{
                                                            style: {
                                                                textAlign:
                                                                    "center",
                                                                fontWeight:
                                                                    "bold",
                                                            },
                                                        }}
                                                    />
                                                </div>
                                                <Chip
                                                    label={
                                                        action.negative
                                                            ? `${action.points}`
                                                            : `+${action.points}`
                                                    }
                                                    style={{
                                                        backgroundColor:
                                                            action.negative
                                                                ? "#ffebee"
                                                                : "#e8f5e8",
                                                        color: action.negative
                                                            ? "#d32f2f"
                                                            : "#2e7d32",
                                                        fontWeight: "bold",
                                                    }}
                                                    size="small"
                                                />
                                                {action.negative && (
                                                    <Chip
                                                        label={t(
                                                            "loyalty_points_negative"
                                                        )}
                                                        style={{
                                                            backgroundColor:
                                                                "#f44336",
                                                            color: "white",
                                                            fontWeight: "bold",
                                                        }}
                                                        size="small"
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {action.hasRating && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "16px",
                                                    marginTop: "12px",
                                                    padding: "12px",
                                                    backgroundColor: "#fff8e1",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: "bold",
                                                        minWidth: "100px",
                                                    }}
                                                >
                                                    Minimum Rating
                                                </Typography>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    <TextField
                                                        size="small"
                                                        value={action.minRating}
                                                        onChange={(e) =>
                                                            handleRatingChange(
                                                                action.key,
                                                                e.target.value
                                                            )
                                                        }
                                                        type="number"
                                                        style={{
                                                            width: "80px",
                                                        }}
                                                        inputProps={{
                                                            min: 1,
                                                            max: 5,
                                                            step: 0.1,
                                                        }}
                                                    />
                                                    <Typography variant="body2">
                                                        stars or more
                                                    </Typography>
                                                </div>
                                            </div>
                                        )}

                                        {action.hasTimeRange && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "16px",
                                                    marginTop: "12px",
                                                    padding: "12px",
                                                    backgroundColor: "#f5f5f5",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: "bold",
                                                        minWidth: "80px",
                                                    }}
                                                >
                                                    {t(
                                                        "loyalty_points_time_range"
                                                    )}
                                                </Typography>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    <TextField
                                                        size="small"
                                                        label={t(
                                                            "loyalty_points_start_time"
                                                        )}
                                                        type="time"
                                                        value={action.startTime
                                                            .toTimeString()
                                                            .slice(0, 5)}
                                                        onChange={(e) =>
                                                            handleTimeRangeChange(
                                                                action.key,
                                                                "startTime",
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width: "120px",
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                    <Typography>
                                                        {t("loyalty_points_to")}
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        label={t(
                                                            "loyalty_points_end_time"
                                                        )}
                                                        type="time"
                                                        value={action.endTime
                                                            .toTimeString()
                                                            .slice(0, 5)}
                                                        onChange={(e) =>
                                                            handleTimeRangeChange(
                                                                action.key,
                                                                "endTime",
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width: "120px",
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </TabPanel>
                </DialogContent>

                <DialogActions style={{ padding: "16px 24px" }}>
                    <Button
                        onClick={() => setConfigOpen(false)}
                        color="inherit"
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveConfiguration}
                        disabled={saving}
                        style={{
                            backgroundColor: colors.MAIN_COLOR,
                            color: "white",
                        }}
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : null
                        }
                    >
                        {saving ? "Saving..." : "Save Configuration"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* User Detail Modal */}
            <Dialog
                open={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    style: {
                        maxHeight: "90vh",
                        height: "90vh",
                    },
                }}
            >
                <DialogTitle
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div>
                        <Typography variant="h5" style={{ fontWeight: "bold" }}>
                            {t("loyalty_points_user_points_history")}
                        </Typography>
                        {selectedUserDetail && (
                            <Typography
                                variant="subtitle1"
                                color="textSecondary"
                            >
                                {selectedUserDetail.userName ||
                                    selectedUserDetail.name ||
                                    "N/A"}{" "}
                                ({selectedUserDetail.email || "N/A"})
                            </Typography>
                        )}
                    </div>
                    <IconButton onClick={() => setDetailModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    style={{
                        padding: "16px 24px",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {selectedUserDetail && (
                        <div style={{ marginBottom: "24px" }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={3}>
                                    <Card
                                        style={{
                                            padding: "20px",
                                            textAlign: "center",
                                            background:
                                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            color: "white",
                                            borderRadius: "16px",
                                            boxShadow:
                                                "0 8px 32px rgba(102, 126, 234, 0.3)",
                                            transition:
                                                "transform 0.2s ease-in-out",
                                            cursor: "pointer",
                                            height: "140px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(-4px)")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(0px)")
                                        }
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            <StarIcon
                                                style={{
                                                    fontSize: "24px",
                                                    opacity: 0.9,
                                                }}
                                            />
                                        </div>
                                        <Typography
                                            variant="h4"
                                            style={{
                                                fontWeight: "bold",
                                                marginBottom: "4px",
                                                fontSize: "2rem",
                                            }}
                                        >
                                            {selectedUserDetail.pointTotal ||
                                                selectedUserDetail.totalPoint ||
                                                0}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                opacity: 0.9,
                                                fontWeight: "500",
                                            }}
                                        >
                                            {t("loyalty_points_total_points")}
                                        </Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card
                                        style={{
                                            padding: "20px",
                                            textAlign: "center",
                                            background:
                                                "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                            color: "white",
                                            borderRadius: "16px",
                                            boxShadow:
                                                "0 8px 32px rgba(79, 172, 254, 0.3)",
                                            transition:
                                                "transform 0.2s ease-in-out",
                                            cursor: "pointer",
                                            height: "140px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(-4px)")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(0px)")
                                        }
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                style={{ opacity: 0.9 }}
                                            >
                                                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
                                            </svg>
                                        </div>
                                        <Typography
                                            variant="h4"
                                            style={{
                                                fontWeight: "bold",
                                                marginBottom: "4px",
                                                fontSize: "2rem",
                                            }}
                                        >
                                            {selectedUserDetail.count ||
                                                selectedUserDetail.transactionCount ||
                                                0}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                opacity: 0.9,
                                                fontWeight: "500",
                                            }}
                                        >
                                            {t("loyalty_points_transactions")}
                                        </Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card
                                        style={{
                                            padding: "20px",
                                            textAlign: "center",
                                            background:
                                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                            color: "white",
                                            borderRadius: "16px",
                                            boxShadow:
                                                "0 8px 32px rgba(240, 147, 251, 0.3)",
                                            transition:
                                                "transform 0.2s ease-in-out",
                                            cursor: "pointer",
                                            height: "140px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(-4px)")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(0px)")
                                        }
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            {selectedUserDetail.type ===
                                            "customer" ? (
                                                <GroupIcon
                                                    style={{
                                                        fontSize: "24px",
                                                        opacity: 0.9,
                                                    }}
                                                />
                                            ) : (
                                                <CarIcon
                                                    style={{
                                                        fontSize: "24px",
                                                        opacity: 0.9,
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <Typography
                                            variant="h5"
                                            style={{
                                                fontWeight: "bold",
                                                marginBottom: "4px",
                                                fontSize: "1.5rem",
                                            }}
                                        >
                                            {selectedUserDetail.type ===
                                            "customer"
                                                ? "Customer"
                                                : "Driver"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                opacity: 0.9,
                                                fontWeight: "500",
                                            }}
                                        >
                                            {t("loyalty_points_user_type")}
                                        </Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card
                                        style={{
                                            padding: "20px",
                                            textAlign: "center",
                                            background:
                                                "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                                            color: "white",
                                            borderRadius: "16px",
                                            boxShadow:
                                                "0 8px 32px rgba(250, 112, 154, 0.3)",
                                            transition:
                                                "transform 0.2s ease-in-out",
                                            cursor: "pointer",
                                            height: "140px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(-4px)")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.transform =
                                                "translateY(0px)")
                                        }
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                style={{ opacity: 0.9 }}
                                            >
                                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                            </svg>
                                        </div>
                                        <Typography
                                            variant="body1"
                                            style={{
                                                fontWeight: "bold",
                                                marginBottom: "4px",
                                                fontSize: "1rem",
                                                lineHeight: "1.2",
                                            }}
                                        >
                                            {selectedUserDetail.lastActivity
                                                ? new Date(
                                                      selectedUserDetail.lastActivity
                                                  ).toLocaleDateString(
                                                      "en-US",
                                                      {
                                                          month: "short",
                                                          day: "numeric",
                                                          year: "numeric",
                                                      }
                                                  )
                                                : "N/A"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                opacity: 0.9,
                                                fontWeight: "500",
                                            }}
                                        >
                                            {t("loyalty_points_last_activity")}
                                        </Typography>
                                    </Card>
                                </Grid>
                            </Grid>
                        </div>
                    )}

                    <Typography
                        variant="h6"
                        style={{ fontWeight: "bold", marginBottom: "16px" }}
                    >
                        {t("loyalty_points_points_history")}
                    </Typography>

                    {userHistoryLoading && userHistory.length === 0 ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "200px",
                            }}
                        >
                            <CircularProgress />
                        </div>
                    ) : (
                        <div style={{ flex: 1, overflow: "hidden" }}>
                            <TableContainer
                                style={{
                                    height: "100%",
                                    maxHeight: "calc(90vh - 350px)",
                                }}
                            >
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <strong>
                                                    {t("loyalty_points_date")}
                                                </strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>
                                                    {t("loyalty_points_points")}
                                                </strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>
                                                    {t(
                                                        "loyalty_points_description"
                                                    )}
                                                </strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>
                                                    {t("loyalty_points_status")}
                                                </strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {userHistory.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={4}
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "40px",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {t(
                                                            "loyalty_points_no_history_available"
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <>
                                                {userHistory.map(
                                                    (history, index) => (
                                                        <TableRow
                                                            key={
                                                                history.id ||
                                                                index
                                                            }
                                                            hover
                                                        >
                                                            <TableCell>
                                                                {new Date(
                                                                    history.createdAt ||
                                                                        history.created_at ||
                                                                        history.lastActivity ||
                                                                        history.last_activity
                                                                ).toLocaleDateString()}{" "}
                                                                {new Date(
                                                                    history.createdAt ||
                                                                        history.created_at ||
                                                                        history.lastActivity ||
                                                                        history.last_activity
                                                                ).toLocaleTimeString()}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div
                                                                    style={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        gap: "4px",
                                                                    }}
                                                                >
                                                                    <StarIcon
                                                                        style={{
                                                                            color: "#FFD700",
                                                                            fontSize:
                                                                                "16px",
                                                                        }}
                                                                    />
                                                                    <span
                                                                        style={{
                                                                            color:
                                                                                history.points >
                                                                                0
                                                                                    ? "#4caf50"
                                                                                    : "#f44336",
                                                                            fontWeight:
                                                                                "bold",
                                                                        }}
                                                                    >
                                                                        {history.points >
                                                                        0
                                                                            ? "+"
                                                                            : ""}
                                                                        {
                                                                            history.points
                                                                        }
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                {getTranslationForHistoryPointsDescription(
                                                                    history.description ||
                                                                        history.action ||
                                                                        history.reason
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={t(
                                                                        history.status ||
                                                                            "completed"
                                                                    )}
                                                                    color={
                                                                        history.status ===
                                                                            "Completed" ||
                                                                        history.status ===
                                                                            "completed"
                                                                            ? "success"
                                                                            : "default"
                                                                    }
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: "16px 0px", width: "100%" }}>
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        {/* User History Pagination */}
                        {userHistoryPagination.total_pages > 0 && (
                            <div
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: "16px",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {t("show")}{" "}
                                    {(userHistoryPagination.current_page - 1) *
                                        userHistoryPagination.per_page +
                                        1}{" "}
                                    {t("to")}{" "}
                                    {Math.min(
                                        userHistoryPagination.current_page *
                                            userHistoryPagination.per_page,
                                        userHistoryPagination.total
                                    )}{" "}
                                    {t("of")} {userHistoryPagination.total}
                                </Typography>
                                <Pagination
                                    count={userHistoryPagination.total_pages}
                                    page={userHistoryPagination.current_page}
                                    onChange={(event, page) =>
                                        handleUserHistoryPageChange(page)
                                    }
                                    color="primary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                    disabled={userHistoryLoading}
                                />
                            </div>
                        )}
                    </div>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() =>
                    setNotification({ ...notification, open: false })
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() =>
                        setNotification({ ...notification, open: false })
                    }
                    severity={notification.type}
                    sx={{ width: "100%" }}
                >
                    {notification.message}
                    {notification.type === "error" && retryCount > 0 && (
                        <div style={{ marginTop: "8px" }}>
                            <Button
                                size="small"
                                onClick={() => window.location.reload()}
                                style={{ color: "white" }}
                            >
                                {t("refresh_page")}
                            </Button>
                        </div>
                    )}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default LoyaltyPoints;
